(function () {
  if (typeof window.CollectionListTabsSection !== 'undefined') return;

  const SWIPER_DESKTOP = 1023;
  const SWIPER_TABLET = 850;

  function getTabOffsetLeft(tab, container) {
    const tabs = Array.from(container.querySelectorAll('.collection-list-tabs__tab'));
    const index = tabs.indexOf(tab);
    if (index < 0) return 0;

    const gap = parseFloat(getComputedStyle(container).columnGap || getComputedStyle(container).gap) || 8;
    let offset = parseFloat(getComputedStyle(tabs[0]).marginLeft) || 0;

    for (let i = 0; i < index; i++) {
      offset += tabs[i].getBoundingClientRect().width + gap;
    }

    return offset;
  }

  function scrollActiveTabMobile(activeTab) {
    if (!activeTab || window.innerWidth > 749) return;

    const container = activeTab.closest('.collection-list-tabs__nav');
    if (!container) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const containerWidth = container.clientWidth;
        const activeLeft = getTabOffsetLeft(activeTab, container);
        const activeWidth = activeTab.getBoundingClientRect().width;
        const nextTab = activeTab.nextElementSibling;
        const prevTab = activeTab.previousElementSibling;
        let scrollLeft;

        if (nextTab) {
          const nextLeft = getTabOffsetLeft(nextTab, container);
          const nextWidth = nextTab.getBoundingClientRect().width;
          const centerScroll = activeLeft + activeWidth / 2 - containerWidth / 2;
          const nextPeekScroll = nextLeft + nextWidth * 0.5 - containerWidth;
          scrollLeft = Math.max(centerScroll, nextPeekScroll);

          if (prevTab) {
            const prevLeft = getTabOffsetLeft(prevTab, container);
            const prevWidth = prevTab.getBoundingClientRect().width;
            scrollLeft = Math.max(scrollLeft, prevLeft + prevWidth - 24);
          }
        } else {
          const lastMargin = parseFloat(getComputedStyle(activeTab).marginRight) || 16;
          scrollLeft = activeLeft + activeWidth + lastMargin - containerWidth;
        }

        const maxScroll = container.scrollWidth - containerWidth;
        scrollLeft = Math.max(0, Math.min(scrollLeft, maxScroll));
        container.scrollLeft = scrollLeft;
      });
    });
  }

  class CollectionListTabsSection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.swipers = {};
      this.bindTabs();
      this.initSwipers();
      this.activateFirstTab();
    }

    bindTabs() {
      this.root.querySelectorAll('.collection-list-tabs__tab').forEach((tab) => {
        tab.addEventListener('click', (event) => {
          this.openTab(event.currentTarget);
        });
      });

      this._onResize = () => {
        const activeTab = this.root.querySelector('.collection-list-tabs__tab.is-active');
        if (activeTab) scrollActiveTabMobile(activeTab);
        Object.values(this.swipers).forEach((swiper) => {
          if (swiper && typeof swiper.update === 'function') swiper.update();
        });
      };

      window.addEventListener('resize', this._onResize);
    }

    openTab(tab) {
      const panelId = tab.getAttribute('aria-controls');
      if (!panelId) return;

      this.root.querySelectorAll('.collection-list-tabs__tab').forEach((item) => {
        item.classList.remove('is-active');
        item.setAttribute('aria-selected', 'false');
      });

      this.root.querySelectorAll('.collection-list-tabs__panel').forEach((panel) => {
        panel.classList.remove('is-active');
        panel.hidden = true;
      });

      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      const panel = this.root.querySelector('#' + CSS.escape(panelId));
      if (panel) {
        panel.classList.add('is-active');
        panel.hidden = false;
      }

      scrollActiveTabMobile(tab);
      setTimeout(() => scrollActiveTabMobile(tab), 80);
      setTimeout(() => scrollActiveTabMobile(tab), 250);

      const blockId = tab.dataset.blockId;
      if (blockId && this.swipers[blockId]) {
        setTimeout(() => {
          const swiper = this.swipers[blockId];
          swiper.slideTo(0, 0);
          swiper.update();
          this.updateNavVisibility(swiper, blockId);
        }, 50);
      }
    }

    activateFirstTab() {
      const firstTab = this.root.querySelector('.collection-list-tabs__tab');
      if (firstTab) this.openTab(firstTab);
    }

    initSwipers() {
      if (typeof Swiper === 'undefined') return;

      this.root.querySelectorAll('[data-collection-list-swiper]').forEach((el) => {
        const blockId = el.dataset.blockId;
        const nextEl = this.root.querySelector('[data-swiper-next="' + blockId + '"]');
        const prevEl = this.root.querySelector('[data-swiper-prev="' + blockId + '"]');

        const swiper = new Swiper(el, {
          slidesPerView: 2,
          spaceBetween: 25,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: nextEl,
            prevEl: prevEl
          },
          breakpoints: {
            [SWIPER_TABLET]: {
              slidesPerView: 3,
              spaceBetween: 20
            },
            [SWIPER_DESKTOP]: {
              slidesPerView: 4,
              spaceBetween: 25
            }
          }
        });

        this.swipers[blockId] = swiper;
        this.updateNavVisibility(swiper, blockId);

        swiper.on('init', () => this.updateNavVisibility(swiper, blockId));
        swiper.on('slideChange', () => this.updateNavVisibility(swiper, blockId));
        swiper.on('resize', () => this.updateNavVisibility(swiper, blockId));
        swiper.on('breakpoint', () => this.updateNavVisibility(swiper, blockId));
        swiper.on('update', () => this.updateNavVisibility(swiper, blockId));
      });
    }

    updateNavVisibility(swiper, blockId) {
      const nextEl = this.root.querySelector('[data-swiper-next="' + blockId + '"]');
      const prevEl = this.root.querySelector('[data-swiper-prev="' + blockId + '"]');
      if (!nextEl || !prevEl || !swiper) return;

      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();

      const noOverflow = swiper.isLocked || swiper.snapGrid.length <= 1;
      const canGoPrev = !noOverflow && !swiper.isBeginning;
      const canGoNext = !noOverflow && !swiper.isEnd;

      if (noOverflow) {
        nextEl.style.display = 'none';
        prevEl.style.display = 'none';
        return;
      }

      nextEl.style.display = 'flex';
      prevEl.style.display = 'flex';
      prevEl.classList.toggle('is-disabled', !canGoPrev);
      nextEl.classList.toggle('is-disabled', !canGoNext);
    }

    destroy() {
      window.removeEventListener('resize', this._onResize);
      Object.values(this.swipers).forEach((swiper) => {
        if (swiper && typeof swiper.destroy === 'function') swiper.destroy(true, true);
      });
      this.swipers = {};
    }
  }

  window.CollectionListTabsSection = CollectionListTabsSection;
  window.collectionListTabsInstances = window.collectionListTabsInstances || {};

  function initSection(root) {
    const sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    if (window.collectionListTabsInstances[sectionId]) {
      window.collectionListTabsInstances[sectionId].destroy();
    }

    window.collectionListTabsInstances[sectionId] = new CollectionListTabsSection(root);
  }

  function initAll() {
    document.querySelectorAll('.collection-list-tabs').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('.collection-list-tabs');
    if (root) initSection(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('.collection-list-tabs');
    if (!root) return;
    const sectionId = root.dataset.sectionId;
    if (sectionId && window.collectionListTabsInstances[sectionId]) {
      window.collectionListTabsInstances[sectionId].destroy();
      delete window.collectionListTabsInstances[sectionId];
    }
  });
})();
